import React from 'react'
import { ArrowLeft, Copy, Camera, Link2 } from 'lucide-react'
import { Button } from '../../../../components/ui/button'
import SmartImage from '../../../../components/SmartImage'

export default function ProfileDetailHeader({
  profileData,
  patronProfile,
  verifyB64Display,
  onBack,
  onCopyId,
  onCopyLink,
  onAddToWatchlist,
  onRunAml,
  isRunningAml,
  onOpenMembership,
  isLoadingMembership,
  onOpenGaming,
  onEditProfile,
  canEditProfile,
  onFoldProfile,
  canFoldProfile,
  foldStatus,
  onOpenKeeper,
}) {
  const fullName = [profileData.FN, profileData.MDN || profileData.MN || patronProfile?.middle_name, profileData.LN]
    .map((p) => String(p || '').trim())
    .filter(Boolean)
    .join(' ')
  const membershipNumber = profileData.MembershipNumber || patronProfile?.membership_number
  const playerId = profileData.MID || patronProfile?.member_id

  return (
    <>
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-500 hover:text-teal-700 px-0">
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back to search
        </Button>
      </div>

      <header className="bg-white border-b border-slate-200 px-6 pt-5 pb-4">
        <div className="flex items-start gap-5">
          <div className="h-16 w-16 overflow-hidden rounded-full border border-slate-200 bg-slate-100 flex items-center justify-center shrink-0">
            {verifyB64Display ? (
              <SmartImage
                base64Data={verifyB64Display}
                alt={fullName}
                className="h-16 w-16 object-cover"
                placeholderType="faceprint"
                clickable
                modalTitle={fullName}
              />
            ) : (
              <Camera className="h-7 w-7 text-slate-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-[26px] font-bold m-0 mb-1">{fullName}</h1>
            <div className="flex flex-wrap gap-2 items-center mb-3">
              {profileData.MasterProfileID && (
                <span className="inline-flex items-center gap-1 font-mono text-[11px] bg-slate-100 px-2.5 py-0.5 rounded-full border border-slate-200 text-slate-600">
                  {profileData.MasterProfileID}
                  <button
                    type="button"
                    onClick={(e) => onCopyId(profileData.MasterProfileID, e)}
                    className="text-slate-400 hover:text-slate-600"
                    title="Copy Patron ID"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                  {onCopyLink ? (
                    <button
                      type="button"
                      onClick={(e) => onCopyLink(e)}
                      className="text-slate-400 hover:text-slate-600"
                      title="Copy profile link"
                    >
                      <Link2 className="h-3 w-3" />
                    </button>
                  ) : null}
                </span>
              )}
              {membershipNumber && (
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full border border-blue-200 bg-blue-50 text-blue-800">
                  Member {membershipNumber}
                </span>
              )}
              {playerId && (
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700">
                  Player {playerId}
                </span>
              )}
              {patronProfile?.visitor_id && (
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-800">
                  Visitor {patronProfile.visitor_id}
                </span>
              )}
              {String(patronProfile?.profile_type || '').toUpperCase() === 'VISITOR' && !patronProfile?.visitor_id ? (
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-800">
                  Visitor
                </span>
              ) : null}
              {!verifyB64Display && (
                <span className="text-xs text-slate-500 ml-auto max-sm:ml-0">Photo removed · retention</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {canEditProfile && onEditProfile ? (
                <Button variant="outline" size="sm" onClick={onEditProfile}>
                  Edit profile
                </Button>
              ) : null}
              {canFoldProfile && onFoldProfile && !foldStatus?.folded_into_master_profile_id ? (
                <Button variant="outline" size="sm" onClick={onFoldProfile}>
                  Fold into…
                </Button>
              ) : null}
              <Button size="sm" onClick={onRunAml} disabled={isRunningAml}>
                {isRunningAml ? 'Running check…' : 'Run AML/PEP check'}
              </Button>
              <Button variant="outline" size="sm" onClick={onAddToWatchlist}>
                Add to watchlist
              </Button>
              <Button variant="outline" size="sm" onClick={onOpenMembership} disabled={isLoadingMembership}>
                {isLoadingMembership ? 'Loading…' : 'Membership details'}
              </Button>
              <Button variant="outline" size="sm" onClick={onOpenGaming}>
                Gaming &amp; metering
              </Button>
            </div>
          </div>
        </div>
        {foldStatus?.folded_into_master_profile_id ? (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            This profile is folded into{' '}
            <button
              type="button"
              className="font-semibold underline bg-transparent border-0 p-0 cursor-pointer text-amber-950"
              onClick={() => onOpenKeeper?.(foldStatus.folded_into_master_profile_id)}
            >
              {foldStatus.folded_into_master_profile_id}
            </button>
            . It stays on file for audit and is hidden from search.
          </div>
        ) : null}
        {Array.isArray(foldStatus?.folded_from) && foldStatus.folded_from.length > 0 ? (
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
            Folded into this keeper:{' '}
            {foldStatus.folded_from.map((r) => r.master_profile_id).join(', ')}
          </div>
        ) : null}
      </header>
    </>
  )
}
